/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { enviroment } from 'src/environments/environment';
import { 
  Mantenedor, 
  RespuestaMantenedores, 
  FiltrosMantenedor,
  MantenedorCreateRequest,
  MantenedorUpdateRequest,
  CategoriaMantenedorEntity,
  RespuestaCategorias,
  InformacionHotel,
  InformacionGimnasio,
  InformacionRestaurante
} from '../models/mantenedor.interface';

@Injectable({
  providedIn: 'root',
})
export class MantenedorService {
  private readonly baseUrl = enviroment.url + 'mantenedores';

  constructor(private httpClient: HttpClient) {}

  /**
   * Obtener todas las categorías de mantenedores disponibles
   */
  async getCategorias(): Promise<CategoriaMantenedorEntity[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient.get<RespuestaCategorias>(`${this.baseUrl.replace('mantenedores', 'categorias-mantenedor')}`)
      );
      
      if (response.success) {
        return response.data;
      }
      throw new Error('Error al obtener categorías');
    } catch (error) {
      console.error('Error obteniendo categorías:', error);
      throw error;
    }
  }

  /**
   * Obtiene todos los mantenedores con filtros opcionales
   * @param filtros Filtros de búsqueda
   * @returns Promise<RespuestaMantenedores> Lista paginada de mantenedores
   */
  async getMantenedores(filtros?: FiltrosMantenedor): Promise<RespuestaMantenedores> {
    let params = new HttpParams();
    
    if (filtros) {
      if (filtros.categoria) params = params.set('categoria', filtros.categoria);
      if (filtros.estado) params = params.set('estado', filtros.estado);
      if (filtros.page) params = params.set('page', filtros.page.toString());
      if (filtros.limit) params = params.set('limit', filtros.limit.toString());
      if (filtros.nombre) params = params.set('nombre', filtros.nombre);
    }

    return firstValueFrom<RespuestaMantenedores>(
      this.httpClient.get<RespuestaMantenedores>(this.baseUrl, { params }),
    );
  }

  /**
   * Obtiene mantenedores por categoría específica con paginación
   * @param categoriaId ID de la categoría (1: hoteles, 2: gimnasios, 3: restaurantes)
   * @param page Número de página
   * @param limit Límite de registros por página
   * @returns Promise<RespuestaMantenedores> Lista paginada de mantenedores
   */
  async getMantenedoresByCategoria(categoriaId: number, page = 1, limit = 30): Promise<RespuestaMantenedores> {
    const url = `${this.baseUrl}/categoria/${categoriaId}`;
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return firstValueFrom<RespuestaMantenedores>(
      this.httpClient.get<RespuestaMantenedores>(url, { params })
    );
  }

  /**
   * Obtiene un mantenedor por ID
   * @param id ID del mantenedor
   * @returns Promise<Mantenedor> Datos del mantenedor
   */
  async getMantenedorById(id: number): Promise<Mantenedor> {
    return firstValueFrom<Mantenedor>(
      this.httpClient.get<Mantenedor>(`${this.baseUrl}/${id}`),
    );
  }

  /**
   * Crea un nuevo mantenedor
   * @param mantenedor Datos del mantenedor a crear
   * @returns Promise<any> Respuesta del servidor
   */
  async createMantenedor(mantenedor: MantenedorCreateRequest): Promise<any> {
    return firstValueFrom<any>(
      this.httpClient.post<any>(this.baseUrl, mantenedor),
    );
  }

  /**
   * Actualiza un mantenedor existente
   * @param id ID del mantenedor
   * @param mantenedor Datos del mantenedor a actualizar
   * @returns Promise<any> Respuesta del servidor
   */
  async updateMantenedor(id: number, mantenedor: MantenedorUpdateRequest): Promise<any> {
    return firstValueFrom<any>(
      this.httpClient.put<any>(`${this.baseUrl}/${id}`, mantenedor),
    );
  }

  /**
   * Elimina un mantenedor (soft delete)
   * @param id ID del mantenedor a eliminar
   * @returns Promise<any> Respuesta del servidor
   */
  async deleteMantenedor(id: number): Promise<any> {
    return firstValueFrom<any>(
      this.httpClient.delete<any>(`${this.baseUrl}/${id}`),
    );
  }

  /**
   * Cambia el estado de un mantenedor (activo/inactivo)
   * @param id ID del mantenedor
   * @param estado Nuevo estado del mantenedor
   * @returns Promise<any> Respuesta del servidor
   */
  async changeMantenedorStatus(id: number, estado: 'activo' | 'inactivo'): Promise<any> {
    // Para cambiar solo el estado, necesitamos obtener primero la información completa
    const mantenedor = await this.getMantenedorById(id);
    const updateData: MantenedorUpdateRequest = {
      informacion: mantenedor.informacion as any,
      estado
    };
    return this.updateMantenedor(id, updateData);
  }

  /**
   * Obtiene las categorías disponibles para mantenedores
   * @returns string[] Lista de categorías disponibles
   */
  getCategoriesAvailable(): string[] {
    return ['hotel', 'gimnasio', 'restaurante'];
  }

  /**
   * Valida la estructura de información según la categoría
   * @param categoria Categoría del mantenedor
   * @param informacion Información a validar
   * @returns boolean Indica si la información es válida
   */
  validateInformationStructure(categoria: string, informacion: any): boolean {
    const requiredFields: { [key: string]: string[] } = {
      'hotel': ['ciudad', 'pais', 'direccion', 'categoria_estrellas'],
      'gimnasio': ['ciudad', 'direccion', 'horario_apertura', 'horario_cierre'],
      'restaurante': ['ciudad', 'direccion', 'tipo_cocina', 'horario_apertura', 'horario_cierre'],
      'contrato_colectivo': ['fecha_inicio', 'fecha_termino'],
      'gicelquisco': ['id', 'descripcion']
    };
    
    const fields = requiredFields[categoria];
    if (!fields) return false;

    return fields.every(field => Object.prototype.hasOwnProperty.call(informacion, field) && informacion[field] !== null && informacion[field] !== '');
  }
}